import { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import type {
  CalibrationMatrixFilters,
  ConsultantGrade,
} from '@/api/types/calibrationMatrix.types';
import { ANALYTICS_VALUE, GRADE_OPTIONS } from './lookups';

interface TcOption {
  id: number;
  name: string;
}

interface CalibrationMatrixFilterPanelProps {
  filters: CalibrationMatrixFilters;
  onChange: (next: CalibrationMatrixFilters) => void;
  geosOptions: string[];
  sectorsOptions: string[];
  tcOptions: TcOption[];
  visibilityCaption?: string;
  isUpdating?: boolean;
}

export function CalibrationMatrixFilterPanel({
  filters,
  onChange,
  geosOptions,
  sectorsOptions,
  tcOptions,
  visibilityCaption,
  isUpdating = false,
}: CalibrationMatrixFilterPanelProps) {
  const handleGradeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onChange({ ...filters, grade: value === '' ? null : (value as ConsultantGrade) });
  };

  const handleAnalyticsChange = (event: SelectChangeEvent<string>) => {
    const raw = event.target.value;
    if (raw === '') {
      onChange({ ...filters, analytics: null });
      return;
    }
    const analytics = raw === 'true';
    // FR-2: Sector is linked to Analytics.
    let nextSector = filters.sector;
    if (analytics) {
      nextSector = ANALYTICS_VALUE;
    } else if (filters.sector === ANALYTICS_VALUE) {
      nextSector = null;
    }
    onChange({ ...filters, analytics, sector: nextSector });
  };

  const handleSectorChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onChange({ ...filters, sector: value === '' ? null : value });
  };

  const handleGeosChange = (_: unknown, value: string[]) => {
    onChange({ ...filters, geos: value });
  };

  const handleTcChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onChange({
      ...filters,
      talentCommitteeId: value === '' ? null : Number(value),
    });
  };

  const sectorLocked = filters.analytics === true;
  const sectorOptionsForRender = useMemo(() => {
    const base =
      filters.analytics === false
        ? sectorsOptions.filter((s) => s !== ANALYTICS_VALUE)
        : sectorsOptions;
    const current = filters.sector;
    if (current && !base.includes(current)) {
      return [current, ...base];
    }
    return base;
  }, [filters.analytics, filters.sector, sectorsOptions]);

  return (
    <Box>
      <Stack
        direction="column"
        spacing={2}
        data-testid="calibration-matrix-filter-panel"
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          flexWrap="wrap"
          useFlexGap
        >
        <FormControl
          size="small"
          sx={{ minWidth: 140 }}
          required
          data-testid="calibration-matrix-filter-grade"
        >
          <InputLabel id="cm-grade-label">Grade</InputLabel>
          <Select
            labelId="cm-grade-label"
            label="Grade"
            value={filters.grade ?? ''}
            onChange={handleGradeChange}
            inputProps={{ 'aria-label': 'Grade', 'aria-required': true }}
          >
            {GRADE_OPTIONS.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 180 }}
          required
          data-testid="calibration-matrix-filter-analytics"
        >
          <InputLabel id="cm-analytics-label">Analytics</InputLabel>
          <Select
            labelId="cm-analytics-label"
            label="Analytics"
            value={filters.analytics === null ? '' : String(filters.analytics)}
            onChange={handleAnalyticsChange}
            inputProps={{ 'aria-label': 'Analytics', 'aria-required': true }}
          >
            <MenuItem value="true">Analytics</MenuItem>
            <MenuItem value="false">Non-Analytics</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          size="small"
          sx={{ minWidth: 220 }}
          options={geosOptions}
          value={filters.geos}
          onChange={handleGeosChange}
          data-testid="calibration-matrix-filter-geo"
          renderInput={(params) => (
            <TextField
              {...params}
              label="GEO"
              placeholder={filters.geos.length === 0 ? 'All' : ''}
              inputProps={{
                ...params.inputProps,
                'aria-label': 'GEO',
              }}
            />
          )}
        />

        <FormControl
          size="small"
          sx={{ minWidth: 180 }}
          disabled={sectorLocked}
          data-testid="calibration-matrix-filter-sector"
        >
          <InputLabel id="cm-sector-label">Sector</InputLabel>
          <Select
            labelId="cm-sector-label"
            label="Sector"
            value={filters.sector ?? ''}
            onChange={handleSectorChange}
            disabled={sectorLocked}
            inputProps={{ 'aria-label': 'Sector' }}
          >
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {sectorOptionsForRender.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 280 }}
          data-testid="calibration-matrix-filter-talent-committee"
        >
          <InputLabel id="cm-tc-label">Talent Committee</InputLabel>
          <Select
            labelId="cm-tc-label"
            label="Talent Committee"
            value={filters.talentCommitteeId === null ? '' : String(filters.talentCommitteeId)}
            onChange={handleTcChange}
            inputProps={{ 'aria-label': 'Talent Committee' }}
          >
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {tcOptions.map((tc) => (
              <MenuItem key={tc.id} value={String(tc.id)}>
                {tc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
        >
          {visibilityCaption && (
            <Typography variant="body2" color="text.secondary">
              {visibilityCaption}
            </Typography>
          )}
          {isUpdating && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              data-testid="calibration-matrix-updating-indicator"
            >
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary">
                Updating…
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export default CalibrationMatrixFilterPanel;
